import React from 'react';
import Modal from 'react-modal'; // Or your preferred modal library
import { UserProfile } from '../services/profileService'; // Import the type
import CopyIcon from '../icons/copy.svg'; // Reuse copy icon

interface UserProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    userProfile: UserProfile;
    currentUserAddress?: string | null; // Address of the logged-in user
    onAddContact: (user: UserProfile) => void;
    onSendMessage: (user: UserProfile) => void;
    isContact: (address: string) => boolean; // Function to check if user is already a contact
}

// Basic modal styling (customize as needed)
const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#1a202c', // Dark background
        color: 'white',
        padding: '2rem',
        borderRadius: '10px',
        border: '1px solid #4A5568',
        maxWidth: '400px',
        width: '90%',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 1000, // Ensure modal is on top
    },
};

Modal.setAppElement('#root'); // Or your app's root element ID

const UserProfileModal: React.FC<UserProfileModalProps> = ({
    isOpen,
    onClose,
    userProfile,
    currentUserAddress,
    onAddContact,
    onSendMessage,
    isContact,
}) => {

    const handleCopyAddress = async () => {
        try {
            await navigator.clipboard.writeText(userProfile.walletAddress);
            // Optional: Show feedback
            console.log("Address copied:", userProfile.walletAddress);
        } catch (err) {
            console.error('Failed to copy address: ', err);
        }
    };

    const isOwnProfile = currentUserAddress?.toLowerCase() === userProfile.walletAddress.toLowerCase();
    const alreadyContact = isContact(userProfile.walletAddress);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={customStyles}
            contentLabel="User Profile"
        >
            <div className="flex flex-col items-center">
                <img
                    src={`/profile-pictures/${userProfile.profilePicture || 'profil1.jpg'}`} // Adjust path as needed
                    alt={userProfile.username}
                    className="w-24 h-24 rounded-full mb-4 border-2 border-gray-500"
                />
                <h2 className="text-2xl font-bold mb-2">{userProfile.username}</h2>
                <div className="flex items-center text-gray-400 mb-6 text-sm">
                    <span>{`${userProfile.walletAddress.substring(0, 6)}...${userProfile.walletAddress.substring(userProfile.walletAddress.length - 4)}`}</span>
                    <button onClick={handleCopyAddress} className="ml-2 p-1 hover:bg-gray-600 rounded">
                        <img src={CopyIcon} alt="Copy Address" className="w-4 h-4" />
                    </button>
                </div>

                {!isOwnProfile && currentUserAddress && ( // Only show buttons if viewing another user and logged in
                    <div className="flex gap-4 w-full justify-center">
                        <button
                            onClick={() => onSendMessage(userProfile)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white font-semibold"
                        >
                            Send Message
                        </button>
                        <button
                            onClick={() => onAddContact(userProfile)}
                            disabled={alreadyContact}
                            className={`px-4 py-2 rounded text-white font-semibold ${alreadyContact
                                ? 'bg-gray-500 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700'
                                }`}
                        >
                            {alreadyContact ? 'Contact Added' : 'Add Contact'}
                        </button>
                    </div>
                )}
                 {isOwnProfile && (
                     <p className="text-sm text-gray-500 mt-4">(This is your profile)</p>
                 )}
                 {!currentUserAddress && (
                     <p className="text-sm text-yellow-500 mt-4">Connect wallet to interact</p>
                 )}
            </div>
            <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-white text-2xl">&times;</button>
        </Modal>
    );
};

export default UserProfileModal;
